/**
 * Filter Utility Functions
 * Common helper functions for working with the new filter system
 */

import { Geometry } from 'geojson';
import {
  FilterField,
  FilterGroup,
  FilterOperator,
  PaginationConfig,
  QueryConfig,
  ScopeConfig,
  SortConfig,
  SortDirection,
  SpatialDistanceValue
} from '../models/filter.models';
import { FilterBuilderUtil } from './filter-builder.util';

/**
 * Common filter utility functions
 */
export class FilterUtils {
  /**
   * Creates a filter for exact text match
   */
  static textEquals(field: string, value: string): FilterField {
    return FilterBuilderUtil.text(field).equals(value);
  }

  /**
   * Creates a filter for text contains (case-insensitive)
   */
  static textContains(field: string, value: string): FilterField {
    return FilterBuilderUtil.text(field).ilike(value);
  }

  /**
   * Creates a filter for numeric equality
   */
  static numberEquals(field: string, value: number): FilterField {
    return FilterBuilderUtil.numeric(field).equals(value);
  }

  /**
   * Creates a filter for numeric range
   */
  static numberRange(field: string, min: number, max: number): FilterField[] {
    return FilterBuilderUtil.numeric(field).between(min, max);
  }

  /**
   * Creates a filter for date range
   */
  static dateRange(
    field: string,
    start: Date | string,
    end: Date | string
  ): FilterField[] {
    return FilterBuilderUtil.date(field).between(start, end);
  }

  /**
   * Creates a filter for boolean true
   */
  static isTrue(field: string): FilterField {
    return FilterBuilderUtil.boolean(field).isTrue();
  }

  /**
   * Creates a filter for boolean false
   */
  static isFalse(field: string): FilterField {
    return FilterBuilderUtil.boolean(field).isFalse();
  }

  /**
   * Creates a filter for null values
   */
  static isNull(field: string): FilterField {
    return { field, operator: FilterOperator.IS_NULL, value: null };
  }

  /**
   * Creates a filter for non-null values
   */
  static isNotNull(field: string): FilterField {
    return { field, operator: FilterOperator.IS_NOT_NULL, value: null };
  }

  /**
   * Creates a filter for value in list
   */
  static inList(field: string, values: any[]): FilterField {
    return { field, operator: FilterOperator.IN, value: values };
  }

  /**
   * Creates a filter for value not in list
   */
  static notInList(field: string, values: any[]): FilterField {
    return { field, operator: FilterOperator.NOT_IN, value: values };
  }

  /**
   * Creates a spatial filter for intersection
   */
  static spatialIntersects(
    field: string,
    geometry: Geometry
  ): FilterField {
    return FilterBuilderUtil.spatial(field).intersects(geometry);
  }

  /**
   * Creates a spatial filter for within distance
   */
  static spatialWithinDistance(
    field: string,
    geometry: Geometry,
    distance: number,
    unit: 'meters' | 'kilometers' | 'feet' | 'miles' = 'meters'
  ): FilterField {
    return FilterBuilderUtil.spatial(field).withinDistance(
      geometry,
      distance,
      unit
    );
  }

  /**
   * Creates a JSON field filter
   */
  static jsonField(
    field: string,
    path: string,
    operator: FilterOperator,
    value: any
  ): FilterField {
    return { field: `${field}.${path}`, operator, value };
  }

  /**
   * Creates a JSON field equals filter
   */
  static jsonEquals(field: string, path: string, value: any): FilterField {
    return FilterBuilderUtil.json(field).path(path).equals(value);
  }

  /**
   * Creates a JSON field exists filter
   */
  static jsonExists(field: string, path: string): FilterField {
    return FilterBuilderUtil.json(field).path(path).exists();
  }

  /**
   * Combines multiple filters with AND logic
   */
  static and(...filters: FilterField[]): FilterGroup {
    return FilterBuilderUtil.and(...filters);
  }

  /**
   * Combines multiple filters with OR logic
   */
  static or(...filters: FilterField[]): FilterGroup {
    return FilterBuilderUtil.or(...filters);
  }

  /**
   * Creates a sort configuration
   */
  static sortBy(
    field: string,
    direction: SortDirection = SortDirection.ASC
  ): SortConfig {
    return FilterBuilderUtil.sort(field, direction);
  }

  /**
   * Creates pagination configuration
   */
  static paginate(
    skip: number = 0,
    limit: number = 20,
    cursor?: string
  ): PaginationConfig {
    return { skip, limit, cursor };
  }

  /**
   * Creates a project scope configuration
   */
  static scopeProject(
    projectId: string,
    areaFilters?: {
      filters?: string;
      include_ids?: string[];
      exclude_ids?: string[];
    }
  ): ScopeConfig {
    return {
      scope_type: 'project',
      project_scope: { project_id: projectId },
      area_filters: areaFilters
    };
  }

  /**
   * Creates a folder scope configuration
   */
  static scopeFolder(
    folderId: string,
    includeSubfolders: boolean = false,
    areaFilters?: {
      filters?: string;
      include_ids?: string[];
      exclude_ids?: string[];
    }
  ): ScopeConfig {
    return {
      scope_type: 'folder',
      folder_scope: {
        folder_id: folderId,
        include_subfolders: includeSubfolders
      },
      area_filters: areaFilters
    };
  }

  /**
   * Creates a complete query configuration
   */
  static query(config: {
    filters?: FilterField[] | FilterGroup;
    sorts?: SortConfig[];
    pagination?: PaginationConfig;
    scopes?: ScopeConfig[];
  }): QueryConfig {
    return config;
  }

  /**
   * Validates filter field
   */
  static validateFilter(filter: FilterField): boolean {
    if (!filter.field || !filter.operator) {
      return false;
    }

    // Check for restricted fields
    const restrictedFields = ['user_id', 'tenant_id', 'team_id'];
    if (restrictedFields.includes(filter.field)) {
      console.warn(
        `Filter field '${filter.field}' is restricted and cannot be used`
      );
      return false;
    }

    // Check for null operators that don't need values
    const nullOperators = [FilterOperator.IS_NULL, FilterOperator.IS_NOT_NULL];
    if (nullOperators.includes(filter.operator as FilterOperator)) {
      return true;
    }

    // Check if value is provided for operators that need it
    if (filter.value === undefined || filter.value === null) {
      console.warn(`Filter operator '${filter.operator}' requires a value`);
      return false;
    }

    return true;
  }

  /**
   * Validates filter group
   */
  static validateFilterGroup(filterGroup: FilterGroup): boolean {
    if (
      !filterGroup.operator ||
      !filterGroup.conditions ||
      filterGroup.conditions.length === 0
    ) {
      return false;
    }

    return filterGroup.conditions.every((condition) => {
      if ('operator' in condition && 'conditions' in condition) {
        // It's a FilterGroup
        return this.validateFilterGroup(condition as FilterGroup);
      } else {
        // It's a FilterField
        return this.validateFilter(condition as FilterField);
      }
    });
  }

  /**
   * Sanitizes filter field values to prevent injection
   */
  static sanitizeFilterValue(value: any): any {
    if (typeof value === 'string') {
      // Escape SQL wildcards
      return value.replace(/[%_\\]/g, '\\$&');
    }
    return value;
  }

  /**
   * Creates a GeoJSON Point geometry
   */
  static createPoint(longitude: number, latitude: number): Geometry {
    return {
      type: 'Point',
      coordinates: [longitude, latitude]
    };
  }

  /**
   * Creates a GeoJSON Polygon geometry
   */
  static createPolygon(coordinates: number[][][]): Geometry {
    return {
      type: 'Polygon',
      coordinates
    };
  }

  /**
   * Creates a GeoJSON bounding box from min/max coordinates
   */
  static createBoundingBox(
    minLng: number,
    minLat: number,
    maxLng: number,
    maxLat: number
  ): Geometry {
    return {
      type: 'Polygon',
      coordinates: [
        [
          [minLng, minLat],
          [maxLng, minLat],
          [maxLng, maxLat],
          [minLng, maxLat],
          [minLng, minLat]
        ]
      ]
    };
  }

  /**
   * Converts meters to other distance units
   */
  static convertDistance(
    distance: number,
    fromUnit: 'meters' | 'kilometers' | 'feet' | 'miles',
    toUnit: 'meters' | 'kilometers' | 'feet' | 'miles'
  ): number {
    const conversions = {
      meters: 1,
      kilometers: 1000,
      feet: 0.3048,
      miles: 1609.34
    };

    const meters = distance * conversions[fromUnit];
    return meters / conversions[toUnit];
  }

  /**
   * Creates a spatial distance value
   */
  static createSpatialDistance(
    geometry: Geometry,
    distance: number,
    unit: 'meters' | 'kilometers' | 'feet' | 'miles' = 'meters'
  ): SpatialDistanceValue {
    return { geometry, distance, unit };
  }

  /**
   * Merges multiple query configurations
   */
  static mergeQueries(...queries: Partial<QueryConfig>[]): QueryConfig {
    const merged: QueryConfig = {};

    for (const query of queries) {
      if (query.filters) {
        if (Array.isArray(query.filters)) {
          merged.filters = [
            ...((merged.filters as FilterField[]) || []),
            ...query.filters
          ];
        } else {
          // For FilterGroup, we need to handle merging differently
          merged.filters = query.filters;
        }
      }

      if (query.sorts) {
        merged.sorts = [...(merged.sorts || []), ...query.sorts];
      }

      if (query.pagination) {
        merged.pagination = { ...merged.pagination, ...query.pagination };
      }

      if (query.scopes) {
        merged.scopes = [...(merged.scopes || []), ...query.scopes];
      }
    }

    return merged;
  }

  /**
   * Creates a filter for recent items (last N days)
   */
  static recentItems(field: string, days: number): FilterField {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return FilterBuilderUtil.date(field).after(cutoffDate);
  }

  /**
   * Creates a filter for items created today
   */
  static today(field: string): FilterField[] {
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const endOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + 1
    );

    return FilterBuilderUtil.date(field).between(startOfDay, endOfDay);
  }

  /**
   * Creates a filter for items created this week
   */
  static thisWeek(field: string): FilterField[] {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7);

    return FilterBuilderUtil.date(field).between(startOfWeek, endOfWeek);
  }

  /**
   * Creates a filter for items created this month
   */
  static thisMonth(field: string): FilterField[] {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

    return FilterBuilderUtil.date(field).between(startOfMonth, endOfMonth);
  }
}
